import React from 'react';
import { useNavigate } from 'react-router-dom';
import './PivotTable.css';
import { filterPivotRows } from '../utils/pivotUtils';

const PivotTable = ({ 
  pivotData, 
  selectedCity = 'Toronto',
  selectedArea = '',
  postalCodeFilter = '',
  onCellClick // Optional custom handler, otherwise uses default navigation
}) => {
  const navigate = useNavigate();

  // Default cell click handler that navigates to chat page
  const defaultHandleCellClick = (propertyType, bedroomCount, cellData) => {
    if (!cellData || cellData.count === 0) return;

    // Build search parameters for chat page
    const searchParams = new URLSearchParams({
      property_type: propertyType,
      bedrooms: bedroomCount,
      count: cellData.count || '0',
      avg_rent: Math.round(cellData.average_rent) || '0',
      min_rent: Math.round(cellData.min_rent) || '0',
      max_rent: Math.round(cellData.max_rent) || '0',
      user_role: 'tenant',
      sources: 'realtor,facebook,kijiji'
    });

    // Add location parameters
    if (postalCodeFilter && postalCodeFilter.trim().length >= 3) {
      searchParams.append('postal_code', postalCodeFilter.trim().toUpperCase());
      searchParams.append('location_display', postalCodeFilter.trim().toUpperCase());
    } else {
      if (selectedCity) {
        searchParams.append('city', selectedCity);
      }
      if (selectedArea) {
        searchParams.append('area', selectedArea);
      }
      // Set display name
      searchParams.append('location_display', 
        selectedCity && selectedArea ? `${selectedArea}, ${selectedCity}` :
        selectedCity ? selectedCity : 'Greater Toronto Area'
      );
    }

    // Navigate to chat page
    navigate(`/chat?${searchParams.toString()}`);
  };

  const handleCellClick = onCellClick || defaultHandleCellClick;

  if (!pivotData || !pivotData.pivot_table) {
    return (
      <div className="pivot-table-container">
        <div className="pivot-summary">
          <div className="summary-header">
            <div className="filter-summary">No data available</div>
          </div>
          <p>Unable to load pivot table data. Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  const { pivot_table, bedrooms } = pivotData;

  // Convert pivot_table to array for easier processing
  const rowEntries = filterPivotRows(
    Object.entries(pivot_table).map(([key, data]) => ({
      key,
      label: data.label,
      property_type: data.property_type,
      bedrooms: data.bedrooms
    }))
  );

  // Calculate totals
  const calculateTotals = () => {
    const rowTotals = {};
    const columnTotals = {};
    let grandTotal = 0;

    // Initialize column totals
    bedrooms.forEach(bedroom => {
      columnTotals[bedroom] = { count: 0, totalRent: 0, validEntries: 0 };
    });

    // Process each row
    rowEntries.forEach(row => {
      let rowCount = 0;
      let rowTotalRent = 0;
      let rowValidEntries = 0;

      bedrooms.forEach(bedroom => {
        const cellData = row.bedrooms[bedroom];
        if (cellData && cellData.count > 0) {
          const count = cellData.count;
          const rent = cellData.average_rent;

          rowCount += count;
          rowTotalRent += rent * count;
          rowValidEntries += count;

          columnTotals[bedroom].count += count;
          columnTotals[bedroom].totalRent += rent * count;
          columnTotals[bedroom].validEntries += count;
        }
      });

      rowTotals[row.key] = {
        count: rowCount,
        average: rowValidEntries > 0 ? rowTotalRent / rowValidEntries : 0
      };

      grandTotal += rowCount;
    });

    // Calculate column averages
    Object.keys(columnTotals).forEach(bedroom => {
      const col = columnTotals[bedroom];
      col.average = col.validEntries > 0 ? col.totalRent / col.validEntries : 0;
    });

    return { rowTotals, columnTotals, grandTotal };
  };

  const { rowTotals, columnTotals, grandTotal } = calculateTotals();

  return (
    <div className="pivot-table-container">
      <table className="pivot-table">
        <thead>
          <tr>
            <th className="property-type-header">Property Type</th>
            {bedrooms.map(bedroom => (
              <th key={bedroom} className="bedroom-header">
                {bedroom === '0' ? '0 BR' : 
                 bedroom === '5+' ? '5+ BR' : 
                 `${bedroom} BR`}
              </th>
            ))}
            <th className="total-header">Total</th>
          </tr>
        </thead>
        <tbody>
          {rowEntries.map(row => (
            <tr key={row.key} className="data-row">
              <td className="property-type-cell">
                {row.label}
              </td>
              {bedrooms.map(bedroom => {
                const cellData = row.bedrooms[bedroom];
                const isEmpty = !cellData || cellData.count === 0;
                
                return (
                  <td 
                    key={`${row.key}-${bedroom}`} 
                    className={`data-cell ${isEmpty ? 'empty-cell' : 'filled-cell'}`}
                    onClick={() => !isEmpty && handleCellClick(row.property_type, bedroom, cellData)}
                    style={{ cursor: isEmpty ? 'default' : 'pointer' }}
                  >
                    {!isEmpty ? (
                      <div className="cell-content">
                        <div className="avg-rent">${Math.round(cellData.average_rent).toLocaleString()}</div>
                        <div className="count">({cellData.count} listings)</div>
                        <div className="range">${Math.round(cellData.min_rent).toLocaleString()} - ${Math.round(cellData.max_rent).toLocaleString()}</div>
                      </div>
                    ) : (
                      <div className="empty-content">-</div>
                    )}
                  </td>
                );
              })}
              <td className="total-cell">
                {rowTotals[row.key]?.count > 0 ? (
                  <div className="cell-content">
                    <div className="avg-rent">${Math.round(rowTotals[row.key].average).toLocaleString()}</div>
                    <div className="count">({rowTotals[row.key].count} total)</div>
                  </div>
                ) : (
                  <div className="empty-content">-</div>
                )}
              </td>
            </tr>
          ))}
          
          {/* Totals Row */}
          <tr className="totals-row">
            <td className="property-type-cell">
              Total
            </td>
            {bedrooms.map(bedroom => (
              <td key={`total-${bedroom}`} className="column-total-cell">
                {columnTotals[bedroom]?.count > 0 ? (
                  <div className="cell-content">
                    <div className="avg-rent">${Math.round(columnTotals[bedroom].average).toLocaleString()}</div>
                    <div className="count">({columnTotals[bedroom].count} total)</div>
                  </div>
                ) : (
                  <div className="empty-content">-</div>
                )}
              </td>
            ))}
            <td className="grand-total-cell">
              <div className="cell-content">
                <div className="grand-total">{grandTotal.toLocaleString()}</div>
                <div className="grand-total-label">({grandTotal} grand total)</div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default PivotTable;
